import { mapActions } from "vuex";

export default {
  computed: {
    /**
     * Check if any field has errors in the form
     * @returns {*}
     */
    isValid() {
      return this.$store.getters["forms/isValid"](this.name);
    },

    /**
     * Get form result pairs { key: value }
     * @returns {*}
     */
    formValues() {
      return this.$store.getters["forms/getValuePairs"](this.name);
    }
  },

  methods: {
    /**
     * Get field from current form
     * @param fieldName
     * @param formName
     * @returns {*}
     */
    getField(fieldName, formName = this.name) {
      return this.$store.getters["forms/getField"](formName, fieldName);
    },

    /**
     * Allow to get current value from a given field/form name
     * @param fieldName
     * @param formName
     * @returns {*}
     */
    getValue(fieldName, formName = this.name) {
      return this.$store.getters["forms/getValue"](formName, fieldName);
    },

    /**
     * Check if field being touched by name within provided form name(focused and blurred)
     * @param formName
     * @param fieldName
     * @returns {boolean}
     */
    isFieldTouched(fieldName, formName = this.name) {
      return this.$store.getters["forms/isFieldTouched"](formName, fieldName);
    },

    /**
     * Check if form being touched (focused and blurred)
     * @param formName
     * @returns {boolean}
     */
    isFormTouched(formName = this.name) {
      const form = this.getForm(formName);

      if (!form) {
        return false;
      }

      return Object.keys(form.fields).some(fieldName =>
        this.isFieldTouched(fieldName, formName)
      );
    },

    /**
     * Getting form values for a particular form
     *
     * Useful when need to get values from one form within another
     * @param formName
     */
    getFormValues(formName) {
      return this.$store.getters["forms/getValuePairs"](formName);
    },

    /**
     * Trigger validate form
     * @param formName
     * @param ignorePristine bool
     */
    validateForm(formName = this.name, ignorePristine = false) {
      this.validateFields({ formName, ignorePristine });
    },

    /**
     * Clear form values by given form name
     * @param formName
     */
    clearForm(formName = this.name) {
      this.resetForm({ formName });
    },

    /**
     * Clear single field value by given form and field names
     * @param formName
     * @param fieldName
     */
    clearField(formName = this.name, fieldName) {
      this.resetField({ formName, fieldName });
    },

    /**
     * Form getter. Allows to get form object by name
     * @param formName
     * @returns {*|Element}
     */
    getForm(formName = this.name) {
      return this.$store.state.forms[formName];
    },

    /**
     * Validation checker for a specific form
     * @returns {function(*=): *}
     */
    isFormValid(formName) {
      return this.$store.getters["forms/isValid"](formName);
    },

    /**
     * Updating meta field for a particular field within a given form name
     * @param fieldName
     * @param metaKey
     * @param metaValue
     * @param formName
     */
    updateMeta(fieldName, metaKey, metaValue, formName = this.name) {
      this.updateFieldMeta({ formName, fieldName, metaKey, metaValue });
    },

    /**
     * Allows to set meta properties to field by providing field/form names
     * for marking as touched/untouched.
     * @param fieldName
     * @param value
     * @param formName
     */
    setFieldTouchedValue(fieldName, value = true, formName = this.name) {
      const metaFields = ["focused", "blurred"];

      for (let metaField of metaFields) {
        this.updateFieldMeta({
          formName,
          fieldName,
          metaKey: metaField,
          metaValue: value
        });
      }
    },

    /**
     * Allows to set meta properties to form by providing form name
     * for marking as touched/untouched.
     * @param formName
     * @param value
     */
    setFormTouchedValue(formName = this.name, value = false) {
      const fieldsNames = Object.keys(this.getForm(formName).fields);

      fieldsNames.forEach(field =>
        this.setFieldTouchedValue(field, value, formName)
      );
    },

    /**
     * Clearing errors for a particular field
     * @param fieldName
     * @param formName
     */
    clearFieldErrors(fieldName, formName = this.name) {
      this.syncErrors({
        formName,
        fieldName,
        errors: []
      });
    },

    /**
     * Removes all of the errors presented in a given form by name
     * @param formName
     */
    clearFormErrors(formName = this.name) {
      const fieldsToArr = Object.keys(this.getForm(formName).fields);
      fieldsToArr.forEach(field => this.clearFieldErrors(field, formName));
    },

    /**
     * Sync errors from server with specific fields
     * @param fieldName
     * @param formName
     */
    syncErrorsFromServer(e, formName = this.name) {
      if (e.status === 422) {
        e.errors.forEach(error => {
          if (error.code_name === "UNPROCESSABLE_ENTITY" && error.meta.field) {
            this.syncErrors({
              formName,
              fieldName: error.meta.field,
              errors: [error.message]
            });
          }
        });
      }
    },

    ...mapActions("forms", [
      "validateFields",
      "resetForm",
      "registerFields",
      "unregisterFields",
      "syncErrors",
      "initializeForm",
      "updateValue",
      "updateValues",
      "registerForm",
      "updateFieldMeta",
      "validateField",
      "resetErrors"
    ])
  }
};
